import { FC, useEffect, useReducer, useRef, useState } from "react";
import TopBar from "./TopBar";
import { makeStyles } from "@material-ui/core";
import ResizableCard from "./Panes/ResizableCard";
import SplitPane from "react-split-pane";
import { AppThemeData, FontSizeMaster, MasterModel, MastersState, ThemeComponent } from "./Types";
import WidgetGallery from "./Panes/WidgetGallery";
import { GetAppThemeProperties, getFontFamilyMaster, getFontSizeMaster, getFontWeightMaster, getIconSizeMaster, GetThemeComponent, updateAppTheme } from "./ConnectionService";
import PropertyPane from "./Panes/PropertyPane";
import { AppThemeContext } from "src/contexts/AppThemeContext";
import ActionDialog, { DialogAction } from "src/components/ui-components/ActionDialog";
import { Helmet } from "react-helmet-async";

const initialMastersState: MastersState = {
  fontFamilies: [],
  fontWeights: [],
  fontSizes: [],
  iconSizes: [],
};

type MastersAction =
  | { type: "SET_FONT_FAMILIES"; payload: MasterModel[] }
  | { type: "SET_FONT_WEIGHTS"; payload: MasterModel[] }
  | { type: "SET_FONT_SIZES"; payload: FontSizeMaster[] }
  | { type: "SET_ICON_SIZES"; payload: MasterModel[] }
  | { type: "CLEAR_ALL" };

export const mastersReducer = (state: MastersState, action: MastersAction): MastersState => {
  switch (action.type) {
    case "SET_FONT_FAMILIES":
      return { ...state, fontFamilies: action.payload };
    case "SET_FONT_WEIGHTS":
      return { ...state, fontWeights: action.payload };
    case "SET_FONT_SIZES":
      return { ...state, fontSizes: action.payload };
    case "SET_ICON_SIZES":
      return { ...state, iconSizes: action.payload };
    case "CLEAR_ALL":
      return initialMastersState;
    default:
      return state;
  }
};

const useStyles = makeStyles(() => ({
  root: {
    maxHeight: '100%',
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  topBarContainer: {
    flex: '0 0 auto',
  },
  panes: {
    flex: 1,
    display: 'flex',
    position: 'relative',
    overflow: 'hidden',
    minHeight: 0,
    height: '100%',
  },
  gutter: {
    background: '#e0e0e0',
    cursor: 'col-resize',
    width: 4,
    zIndex: 1,
    maxHeight: '100%'
  },
}));


const AppThemePage: FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [isChanged, setIsChanged] = useState<boolean>(false);
  const [themeComponentList, setThemeComponentList] = useState<ThemeComponent[]>([]);
  const [selectedComponent, setSelectedComponent] = useState<string | null>(null);
  const [filteredComponentProperties, setFilteredComponentProperties] = useState<AppThemeData>(null);
  const propertiesList = useRef(null);
  const propertiesListBackup = useRef(null);
  const [state, dispatch] = useReducer(mastersReducer, initialMastersState);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [dialogTitle, setDialogTitle] = useState<string | null>("");
  const [dialogMessage, setDialogMessage] = useState<string | null>("");
  const [dialogActions, setDialogActions] = useState<DialogAction[]>([]);
  const classes = useStyles();

  const handleDarkModeChange = () => {
    setIsDarkMode((prev) => !prev);
  };

  const confirmSave = () => {
    setDialogTitle("Confirm Save");
    setDialogMessage("Are you sure you want to save the changes?");
    setDialogActions([
      {
        label: "Yes",
        type: "primary",
        onClick: async () => {
          setDialogOpen(false);
          await handleSave();
        },
      },
      {
        label: "No",
        type: "default",
        onClick: () => setDialogOpen(false),
      },
    ]);
    setDialogOpen(true);
  };

  const confirmRevert = () => {
    setDialogTitle("Confirm Revert");
    setDialogMessage("Are you sure you want to revert the changes?");
    setDialogActions([
      {
        label: "Yes",
        type: "secondary",
        onClick: () => {
          handleRevert();
          setDialogOpen(false);
        },
      },
      {
        label: "No",
        type: "default",
        onClick: () => setDialogOpen(false),
      },
    ]);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setIsLoading(true);
    const { genericThemeProperties = [], iconProperties = [], fontProperties = [] } = filteredComponentProperties;

    propertiesList.current.genericThemeProperties = [
      ...(propertiesList.current.genericThemeProperties || []).filter(
        x => x.themeComponentKey !== selectedComponent
      ),
      ...genericThemeProperties,
    ];

    propertiesList.current.iconProperties = [
      ...(propertiesList.current.iconProperties || []).filter(
        x => x.themeComponentKey !== selectedComponent
      ),
      ...iconProperties,
    ];

    propertiesList.current.fontProperties = [
      ...(propertiesList.current.fontProperties || []).filter(
        x => x.themeComponentKey !== selectedComponent
      ),
      ...fontProperties,
    ];
    const res = await updateAppTheme(propertiesList.current);
    if (res) {
      setIsChanged(false);
      await getData();
    }
    setIsLoading(false);
  };

  const handleRevert = () => {
    if (!propertiesListBackup.current || !selectedComponent) return;

    propertiesList.current = {
      genericThemeProperties: propertiesListBackup.current.genericThemeProperties,
      iconProperties: propertiesListBackup.current.iconProperties,
      fontProperties: propertiesListBackup.current.fontProperties,
    };

    const filteredProps = {
      genericThemeProperties: propertiesListBackup.current.genericThemeProperties?.filter(x => x.themeComponentKey === selectedComponent),
      iconProperties: propertiesListBackup.current.iconProperties?.filter(x => x.themeComponentKey === selectedComponent),
      fontProperties: propertiesListBackup.current.fontProperties?.filter(x => x.themeComponentKey === selectedComponent)
    };

    setFilteredComponentProperties(filteredProps);
    setIsChanged(false);
  };

  const handleSelectedComponentChange = (selectedComponentKey) => {
    if (selectedComponent && filteredComponentProperties && propertiesList.current) {
      const { genericThemeProperties = [], iconProperties = [], fontProperties = [] } = filteredComponentProperties;

      propertiesList.current.genericThemeProperties = [
        ...(propertiesList.current.genericThemeProperties || []).filter(
          x => x.themeComponentKey !== selectedComponent
        ),
        ...genericThemeProperties,
      ];

      propertiesList.current.iconProperties = [
        ...(propertiesList.current.iconProperties || []).filter(
          x => x.themeComponentKey !== selectedComponent
        ),
        ...iconProperties,
      ];

      propertiesList.current.fontProperties = [
        ...(propertiesList.current.fontProperties || []).filter(
          x => x.themeComponentKey !== selectedComponent
        ),
        ...fontProperties,
      ];
    }

    setSelectedComponent(selectedComponentKey);
  };

  const loadMasters = async () => {
    const fontFamilies = await getFontFamilyMaster();
    fontFamilies.sort((a, b) => a.Label.localeCompare(b.Label, undefined, { sensitivity: 'base' }));
    dispatch({ type: "SET_FONT_FAMILIES", payload: fontFamilies });

    const fontWeights = await getFontWeightMaster();
    fontWeights.sort((a, b) => a.Label.localeCompare(b.Label, undefined, { sensitivity: 'base' }));
    dispatch({ type: "SET_FONT_WEIGHTS", payload: fontWeights });

    const fontSizeRes = await getFontSizeMaster();
    fontSizeRes.sort((a, b) => a.value - b.value);
    dispatch({ type: "SET_FONT_SIZES", payload: fontSizeRes });

    const iconSizeRes = await getIconSizeMaster();
    const iconSizes = iconSizeRes.map(obj => obj.IconSize);
    iconSizes.sort((a, b) => a - b);
    dispatch({ type: "SET_ICON_SIZES", payload: iconSizes });
  };


  const getData = async () => {
    const themeComponents = await GetThemeComponent();
    setThemeComponentList(themeComponents);

    const appThemeProps = await GetAppThemeProperties();
    propertiesList.current = JSON.parse(JSON.stringify(appThemeProps));
    propertiesListBackup.current = JSON.parse(JSON.stringify(appThemeProps));
  };

  const updateGenericData = async (type: string, key: string, value: any) => {
    if (filteredComponentProperties.genericThemeProperties.some(x => x.themePropertyKey === key && x.category === type)) {
      setFilteredComponentProperties((prev) => ({
        ...prev,
        genericThemeProperties: prev.genericThemeProperties.map((x) =>
          x.themePropertyKey === key && x.category === type
            ? { ...x, value: value }
            : x
        ),
      }));
      setIsChanged(true);
      return true;
    } else {
      return false;
    }
  };

  const updateGenericColor = async (key: string, category: string, hexColor: string, alpha: number): Promise<boolean> => {
    if (filteredComponentProperties?.genericThemeProperties?.some(x => x.themePropertyKey === key && x.category === category)) {
      setFilteredComponentProperties((prev) => ({
        ...prev,
        genericThemeProperties: prev.genericThemeProperties.map((x) =>
          x.themePropertyKey === key && x.category === category
            ? { ...x, value: hexColor, alpha: Number(alpha.toFixed(2)) }
            : x
        ),
      }));
      setIsChanged(true);
      return true;
    } else {
      return false;
    }
  };

  const updateFontData = async (key: string, family?: string, size?: number, colour?: string, weight?: string, alpha?: number) => {
    if (filteredComponentProperties.fontProperties?.some(x => x.themeFontPropertyKey === key)) {
      setFilteredComponentProperties((prev) => ({
        ...prev,
        fontProperties: prev.fontProperties.map(x => 
          x.themeFontPropertyKey === key ? { ...x, family, size, colour, weight, alpha } : x
        )
      }));
      setIsChanged(true);
      return true;
    } else {
      return false;
    }
  };

  const updateIconData = async (key: string, icon?: string, size?: number, colour?: string, type?: string, alpha?: number) => {
    if (filteredComponentProperties.iconProperties.some(x => x.themeIconPropertyKey === key)) {
      setFilteredComponentProperties((prev) => ({
        ...prev,
        iconProperties: prev.iconProperties.map(x => 
          x.themeIconPropertyKey === key ? { ...x, icon, size, colour, type, alpha } : x
        )
      }));
      setIsChanged(true);
      return true;
    } else {
      return false;
    }
  };

  const loadData = async () => {
    setIsLoading(true);
    await loadMasters();
    await getData();
    setIsLoading(false);
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedComponent && propertiesList && propertiesList.current) {
      const filteredProps = {
        genericThemeProperties: propertiesList.current.genericThemeProperties?.filter(x => x.themeComponentKey === selectedComponent),
        iconProperties: propertiesList.current.iconProperties?.filter(x => x.themeComponentKey === selectedComponent),
        fontProperties: propertiesList.current.fontProperties?.filter(x => x.themeComponentKey === selectedComponent)
      };

      setFilteredComponentProperties(filteredProps);
    }
  }, [selectedComponent]);

  return (
    <AppThemeContext.Provider value={{ updateGenericData, updateGenericColor, updateFontData, masterOptions: state, updateIconData }}>
      <Helmet>
        <title>Theme</title>
      </Helmet>
      <div className={classes.root}>
        {isLoading ? (
          <div style={{ textAlign: "center", marginTop: "20%" }}>
            <h2>Loading...</h2>
          </div>
        ) : (
          <>
            <div className={classes.topBarContainer}>
              <TopBar
                isDarkMode={isDarkMode}
                handleDarkModeChange={handleDarkModeChange}
                isChanged={isChanged}
                handleRevert={confirmRevert}
                handleSave={confirmSave}
              />
            </div>
        
            <div className={classes.panes}>
              <SplitPane
                split="vertical"
                defaultSize="20%"
                minSize={180}
                resizerClassName={classes.gutter}
              >
                <ResizableCard title="Widget Gallery" height="100%">
                  <WidgetGallery
                    themeComponentList={themeComponentList}
                    handleSelectedComponentChange={handleSelectedComponentChange}
                    selectedComponent={selectedComponent}
                  />
                </ResizableCard>
        
                <SplitPane
                  split="vertical"
                  defaultSize="100%"
                  primary="first"
                  minSize={180}
                  resizerClassName={classes.gutter}
                >
                  <ResizableCard title="Properties" height="100%">
                    <PropertyPane
                      propertiesList={filteredComponentProperties}
                      isDarkFilter={isDarkMode}
                    />
                  </ResizableCard>
                  <></>
                  {/* <ResizableCard title="Preview">
                    <RightComponent />
                  </ResizableCard> */}
                </SplitPane>
              </SplitPane>
            </div>
            <ActionDialog
              open={dialogOpen}
              title={dialogTitle}
              message={dialogMessage}
              actions={dialogActions}
              onClose={() => setDialogOpen(false)}
            />
          </>
        )}
      </div>
    </AppThemeContext.Provider>
  );
};

export default AppThemePage;
